import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Field, Input, NumInput, Modal, Confirm, Empty, Pill, Thumb, useToast } from '../ui';
import { newPlanItem } from '../data/entities';
import { planItemFound, planMatches, planProgress, priceLine, mainPhoto } from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * My Fair Plan (spec § 5.7) — what the buyer intends to find, before they go.
 *
 * This is the screen that turns a trade show from browsing into shopping:
 * written in advance, then each item gets ticked off against a real supplier.
 * "Found" is `found || matches.length > 0` (rule PL1), so skipping an item
 * counts as resolved too — the buyer decided they do not need it.
 */
export default function PlanScreen() {
  const toast = useToast();
  const { fair, suppliers, saveFair } = useFairTracker();

  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(null);

  if (!fair) {
    return (
      <div className="ft-page">
        <Empty title={tr("Open one fair first")} action={<Link className="ft-btn" to={p('/fairs')}>{tr("My fairs")}</Link>} />
      </div>
    );
  }

  const plan = fair.plan || [];
  const progress = planProgress(fair);

  const addItem = async (item) => {
    try {
      await saveFair({ ...fair, plan: [...plan, item] });
      setAdding(false);
      toast('Added to your plan');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  const update = async (id, patch) => {
    try {
      await saveFair({
        ...fair,
        plan: plan.map((it) => (it.id === id ? { ...it, ...patch } : it)),
      });
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  const remove = async () => {
    const item = confirming;
    setConfirming(null);
    try {
      await saveFair({ ...fair, plan: plan.filter((it) => it.id !== item.id) });
      toast('Removed');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("My Fair Plan")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {fair.name} · {tr("what I'm going to find")} ·{' '}
          <b>
            {tr(`${progress.found}/${progress.total} found`)}
          </b>
        </p>
      </div>

      {!plan.length && (
        <Empty title={tr("Nothing planned yet")}>
          Write down what you need before you walk in. Each item can then be ticked off against a
          real supplier.
        </Empty>
      )}

      <div className="ft-list">
        {plan.map((item) => {
          const found = planItemFound(item);
          const matches = planMatches(item, suppliers, fair);

          return (
            <article className="ft-card" key={item.id}>
              <header className="ft-planhead">
                <div>
                  <h2 className="ft-h2">{item.name || 'Untitled item'}</h2>
                  <p className="ft-sub" style={{ margin: 0 }}>
                    {[item.target ? `target ${item.target}` : null, item.qty ? `${item.qty} pcs` : null]
                      .filter(Boolean)
                      .join(' · ') || 'No details'}
                  </p>
                </div>
                <Pill tone={found ? 'strong' : 'maybe'}>
                  {found ? `${tr('Found')}${matches.length ? ` · ${matches.length}` : ''}` : tr('Looking')}
                </Pill>
                <button
                  type="button"
                  className="ft-icon-btn"
                  onClick={() => setConfirming(item)}
                  aria-label="Remove plan item"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </header>

              {item.notes && <p className="ft-sub">{item.notes}</p>}

              {matches.length > 0 && (
                <div className="ft-list" style={{ marginTop: 10 }}>
                  {matches.map(({ supplier, product, best }) => (
                    <Link className="ft-minicard" key={`${supplier.id}-${product.id}`} to={p(`/s/${supplier.id}`)}>
                      <Thumb photo={mainPhoto(supplier, product.id)} initials={supplier.company} size={40} />
                      <div className="ft-minicard-main">
                        <b>{supplier.company}</b>
                        <div className="ft-faint">
                          {product.name} · {priceLine(product, fair) || 'no price'}
                          {product.moq ? ` · MOQ ${product.moq}` : ''}
                        </div>
                      </div>
                      {best && <Pill tone="strong">{tr("best price")}</Pill>}
                      <Pill>{product.decision}</Pill>
                    </Link>
                  ))}
                </div>
              )}

              <div className="ft-row" style={{ marginTop: 10 }}>
                <Link className="ft-btn ft-btn-primary" to={`${p('/entry')}?plan=${item.id}`}>
                  {matches.length ? tr('+ Log another supplier') : tr('Found it — log supplier')}
                </Link>
                {found && !matches.length ? (
                  <button type="button" className="ft-btn" onClick={() => update(item.id, { found: false })}>
                    {tr("Mark as still looking")}
                  </button>
                ) : (
                  !matches.length && (
                    <button type="button" className="ft-btn" onClick={() => update(item.id, { found: true })}>
                      {tr("Skip / not needed")}
                    </button>
                  )
                )}
              </div>
            </article>
          );
        })}
      </div>

      <button
        type="button"
        className="ft-btn ft-btn-gold ft-btn-block"
        style={{ marginTop: 14 }}
        onClick={() => setAdding(true)}
      >
        <Icon name="Plus" size={17} /> {tr("Add product to find")}
      </button>

      {adding && <AddPlanItem onSave={addItem} onClose={() => setAdding(false)} />}

      {confirming && (
        <Confirm
          title={`Remove "${confirming.name || 'this item'}"?`}
          message={tr("It comes off your plan. Any suppliers you already logged against it stay.")}
          confirmLabel={tr("Remove")}
          onConfirm={remove}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}

function AddPlanItem({ onSave, onClose }) {
  const [item, setItem] = useState(() => newPlanItem());
  const set = (k) => (v) => setItem((x) => ({ ...x, [k]: v }));

  return (
    <Modal
      title={tr("Add product to find")}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="ft-btn ft-btn-cancel" onClick={onClose}>
            {tr("Cancel")}
          </button>
          <button
            type="button"
            className="ft-btn ft-btn-green"
            disabled={!item.name.trim()}
            onClick={() => onSave(item)}
          >
            {tr("Add")}
          </button>
        </>
      }
    >
      <Field label={tr("Product")}>
        <Input value={item.name} onChange={set('name')} placeholder="LED pendant lamp" autoFocus />
      </Field>
      <div className="ft-grid-2">
        <Field label={tr("Target price (USD)")}>
          <NumInput value={item.target} onChange={set('target')} placeholder="8.00" />
        </Field>
        <Field label={tr("Quantity I need")}>
          <NumInput value={item.qty} onChange={set('qty')} placeholder="2000" />
        </Field>
      </div>
      <Field label={tr("Notes")}>
        <Input value={item.notes} onChange={set('notes')} placeholder="Must be dimmable, UL listed" />
      </Field>
    </Modal>
  );
}
